import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Marketing Pages
import HomePage from "./pages/HomePage";
import PricingPage from "./pages/PricingPage";
import DemoPage from "./pages/DemoPage";
import AboutPage from "./pages/AboutPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import ImpressumPage from "./pages/ImpressumPage";

// Auth Pages
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";

// Studio Public Pages
import StudioPage from "./pages/studio/StudioPage";
import StudioVisualizePage from "./pages/studio/StudioVisualizePage";
import StudioWrapsPage from "./pages/studio/StudioWrapsPage";
import StudioContactPage from "./pages/studio/StudioContactPage";
import StudioResultPage from "./pages/studio/StudioResultPage";

// Standalone Visualizer Page
import VisualizerPage from "./pages/VisualizerPage";

// Admin Dashboard Pages
import DashboardLayout from "./pages/app/DashboardLayout";
import DashboardOverview from "./pages/app/DashboardOverview";
import OnboardingPage from "./pages/app/OnboardingPage";
import WrapsManagement from "./pages/app/WrapsManagement";
import BrandingPage from "./pages/app/BrandingPage";
import PagesEditor from "./pages/app/PagesEditor";
import GalleryPage from "./pages/app/GalleryPage";
import LeadsPage from "./pages/app/LeadsPage";
import RendersPage from "./pages/app/RendersPage";
import BillingPage from "./pages/app/BillingPage";
import SettingsPage from "./pages/app/SettingsPage";
import SetupCompletePage from "./pages/app/SetupCompletePage";

import "@/App.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Marketing Pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          
          {/* Auth Pages */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          
          {/* Studio Public Pages */}
          <Route path="/s/:slug" element={<StudioPage />} />
          <Route path="/s/:slug/visualize" element={<StudioVisualizePage />} />
          <Route path="/s/:slug/wraps" element={<StudioWrapsPage />} />
          <Route path="/s/:slug/contact" element={<StudioContactPage />} />
          <Route path="/s/:slug/result/:jobId" element={<StudioResultPage />} />
          
          {/* Standalone Visualizer (viz.wrapviz.ai/shop-name style) */}
          <Route path="/viz/:shopName" element={<VisualizerPage />} />
          
          {/* Admin Dashboard */}
          <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<DashboardOverview />} />
            <Route path="onboarding" element={<OnboardingPage />} />
            <Route path="wraps" element={<WrapsManagement />} />
            <Route path="branding" element={<BrandingPage />} />
            <Route path="pages" element={<PagesEditor />} />
            <Route path="gallery" element={<GalleryPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="renders" element={<RendersPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          
          {/* Setup Complete (after checkout) */}
          <Route path="/app/setup-complete" element={<ProtectedRoute><SetupCompletePage /></ProtectedRoute>} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
